/**
 * Team Stats — account performance dashboard (admin/owner only)
 *
 * Four tabs: Day · Trends · Tickets · Settings
 *
 * Reachable from the "Team Stats" submenu row under My Team — visible to every
 * user on every account tier (all four modular tiers and legacy alike; no
 * tier, module, or beta gate). The only restriction is ROLE: regular members
 * see an "admins only" state (the page exposes every teammate's individual
 * metrics), enforced here and by resolveTeamStatsScope on the API.
 *
 * Metric semantics (mirrors server/services/team-stats-engine.ts):
 *   • Per-member performance counts HUMAN sends only; automated sends
 *     (messaging rules / AI) roll into the account-wide totals.
 *   • The server buckets all timestamps to the ACCOUNT timezone; this page
 *     only ever sends calendar dates (YYYY-MM-DD).
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
} from "@/components/ui/form";
import {
  Users,
  Timer,
  CheckCircle2,
  Inbox,
  AlertTriangle,
  Settings,
  CalendarDays,
  TrendingUp,
  Ticket,
  Info,
  ShieldAlert,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { canManageTeam } from "@shared/schema";

// Categorical palette — fixed assignment, validated (dataviz six-checks, light
// surface): blue = first-response/primary, emerald = resolve/human, amber =
// automated/other. Identity never rides on color alone (legends + tables).
const CHART_BLUE = "#3b82f6";
const CHART_GREEN = "#10b981";
const CHART_AMBER = "#f59e0b";

// ─── Types (mirror server/routes/team-analytics.ts responses) ────────────────

type MemberDayStats = {
  memberUserId: number;
  name: string;
  role: string;
  firstResponses: { count: number; avgMins: number | null; withinSlaCount: number; slaPct: number | null };
  resolves: { count: number; avgResolveMins: number | null };
  volume: { humanSends: number; taskStatusChanges: number; tasksClosed: number; commentsPosted: number };
  backlog: { assignedOpen: number; overdue: number };
};

type DaySnapshot = {
  date: string;
  timezone: string;
  sla: { firstResponseMins: number; resolveMins: number };
  accountTotals: {
    inboundCount: number;
    answeredCount: number;
    answeredWithinSlaCount: number;
    avgFirstResponseMins: number | null;
    resolvedCount: number;
    avgResolveMins: number | null;
    humanSends: number;
    automatedSends: number;
    openBacklog: number;
    overdueTasks: number;
    missedSlaCount: number;
  };
  members: MemberDayStats[];
};

type TrendResult = {
  grain: "day" | "week" | "month";
  from: string;
  to: string;
  timezone: string;
  account: Array<{
    bucket: string;
    avgFirstResponseMins: number | null;
    avgResolveMins: number | null;
    humanSends: number;
    totalSends: number;
    resolvedCount: number;
    answeredWithinSlaPct: number | null;
  }>;
  byMember: Array<{
    memberUserId: number;
    name: string;
    series: Array<{ bucket: string; avgFirstResponseMins: number | null; humanSends: number; tasksClosed: number }>;
  }>;
};

type TicketStatsResponse = {
  from: string;
  to: string;
  timezone: string;
  category: string | null;
  knownCategories: readonly string[];
  overall: {
    ticketCount: number;
    closedCount: number;
    touchedCount: number;
    avgTimeToCloseMins: number | null;
    avgTimeToFirstTouchMins: number | null;
  };
  byCategory: Array<{
    category: string;
    ticketCount: number;
    closedCount: number;
    touchedCount: number;
    avgTimeToCloseMins: number | null;
    avgTimeToFirstTouchMins: number | null;
  }>;
};

type TeamStatsSettings = {
  firstResponseTargetMins: number;
  resolveTargetMins: number;
  countAutomatedForAccountSla: boolean;
  leaderboardMetric: "first_response" | "resolve" | "volume";
};

// ─── Formatting helpers ───────────────────────────────────────────────────────

function fmtMins(mins: number | null | undefined): string {
  if (mins == null) return "—";
  if (mins < 60) return `${Math.round(mins)}m`;
  if (mins < 60 * 24) {
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(mins / (60 * 24));
  const h = Math.round((mins % (60 * 24)) / 60);
  return h > 0 ? `${d}d ${h}h` : `${d}d`;
}

function localDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function daysAgoStr(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return localDateStr(d);
}

// ─── Small building blocks ────────────────────────────────────────────────────

function KpiCard({
  title,
  value,
  icon: Icon,
  alert,
  sub,
}: {
  title: string;
  value: string;
  icon: React.ElementType;
  alert?: boolean;
  sub?: string;
}) {
  return (
    <Card
      className={alert ? "border-red-400" : ""}
      data-testid={`kpi-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm text-muted-foreground font-medium">{title}</p>
          <Icon className={`h-4 w-4 ${alert ? "text-red-500" : "text-muted-foreground"}`} />
        </div>
        <p className="text-2xl font-bold tracking-tight">{value}</p>
        <div className="mt-1 flex items-center gap-2 min-h-[20px]">
          {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

function LoadingPanel() {
  return (
    <div className="space-y-3" data-testid="loading-team-stats-panel">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

function EmptyPanel({ message }: { message: string }) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-16 text-center"
      data-testid="empty-team-stats-panel"
    >
      <Info className="h-6 w-6 text-muted-foreground" />
      <p className="max-w-sm text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

/** Pass/fail chip vs. an SLA target. Never color-alone — text carries state. */
function SlaChip({ avgMins, targetMins }: { avgMins: number | null; targetMins: number }) {
  if (avgMins == null) return <span className="text-xs text-muted-foreground">—</span>;
  const ok = avgMins <= targetMins;
  return (
    <Badge
      variant="outline"
      className={`text-xs ${ok ? "border-green-500 text-green-700" : "border-red-400 text-red-600"}`}
      data-testid={ok ? "sla-chip-pass" : "sla-chip-fail"}
    >
      {ok ? "On target" : "Over target"}
    </Badge>
  );
}

// ─── Day tab ─────────────────────────────────────────────────────────────────

function DayTab() {
  const [date, setDate] = useState(() => localDateStr(new Date()));
  const { data, isLoading, error } = useQuery<DaySnapshot>({
    queryKey: ["/api/app/team-stats/day", date],
    queryFn: () =>
      fetch(`/api/app/team-stats/day?date=${date}`, { credentials: "include" }).then((r) => {
        if (!r.ok) throw new Error(`Request failed (${r.status})`);
        return r.json();
      }),
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor="team-stats-date" className="text-sm font-medium text-muted-foreground">
          Day
        </label>
        <Input
          id="team-stats-date"
          type="date"
          value={date}
          max={localDateStr(new Date())}
          onChange={(e) => e.target.value && setDate(e.target.value)}
          className="w-44"
          data-testid="input-team-stats-date"
        />
        {data && (
          <span className="text-xs text-muted-foreground">
            Times in {data.timezone.replace(/_/g, " ")}
          </span>
        )}
      </div>

      {isLoading ? (
        <LoadingPanel />
      ) : error || !data ? (
        <EmptyPanel message="Could not load team stats for this day. Try again shortly." />
      ) : (
        <>
          {/* Headline KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard
              title="Avg First Response"
              value={fmtMins(data.accountTotals.avgFirstResponseMins)}
              icon={Timer}
              alert={
                data.accountTotals.avgFirstResponseMins != null &&
                data.accountTotals.avgFirstResponseMins > data.sla.firstResponseMins
              }
              sub={`target ${fmtMins(data.sla.firstResponseMins)}`}
            />
            <KpiCard
              title="Avg Time to Resolve"
              value={fmtMins(data.accountTotals.avgResolveMins)}
              icon={CheckCircle2}
              alert={
                data.accountTotals.avgResolveMins != null &&
                data.accountTotals.avgResolveMins > data.sla.resolveMins
              }
              sub={`target ${fmtMins(data.sla.resolveMins)} · ${data.accountTotals.resolvedCount} resolved`}
            />
            <KpiCard
              title="Messages Answered"
              value={`${data.accountTotals.answeredCount} / ${data.accountTotals.inboundCount}`}
              icon={Inbox}
              sub={`${data.accountTotals.answeredWithinSlaCount} within SLA`}
            />
            <KpiCard
              title="Open Backlog"
              value={String(data.accountTotals.openBacklog)}
              icon={AlertTriangle}
              alert={data.accountTotals.overdueTasks > 0}
              sub={`${data.accountTotals.overdueTasks} overdue tasks`}
            />
          </div>

          {/* Human vs automated split — makes the attribution rule visible */}
          <Card data-testid="card-account-totals">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Account activity</CardTitle>
              <CardDescription>
                Team (human) sends vs. automated sends — per-member stats below count human
                activity only.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-6 text-sm">
              <div>
                <span className="font-semibold">{data.accountTotals.humanSends}</span>{" "}
                <span className="text-muted-foreground">team replies</span>
              </div>
              <div>
                <span className="font-semibold">{data.accountTotals.automatedSends}</span>{" "}
                <span className="text-muted-foreground">automated (rules / AI)</span>
              </div>
              <div>
                <span className="font-semibold">{data.accountTotals.missedSlaCount}</span>{" "}
                <span className="text-muted-foreground">answered over SLA</span>
              </div>
            </CardContent>
          </Card>

          {/* Leaderboard */}
          <Card data-testid="card-leaderboard">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4" /> Team leaderboard
              </CardTitle>
              <CardDescription>
                Ranked by the metric chosen in Settings. Members with no activity for the day
                show zeros.
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {data.members.length === 0 ? (
                <EmptyPanel message="No team members found for this account." />
              ) : (
                <table className="w-full text-sm" data-testid="table-leaderboard">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground border-b">
                      <th className="py-2 pr-3 font-medium">Member</th>
                      <th className="py-2 pr-3 font-medium">First responses</th>
                      <th className="py-2 pr-3 font-medium">Avg response</th>
                      <th className="py-2 pr-3 font-medium">SLA</th>
                      <th className="py-2 pr-3 font-medium">Resolved</th>
                      <th className="py-2 pr-3 font-medium">Avg resolve</th>
                      <th className="py-2 pr-3 font-medium">Sends</th>
                      <th className="py-2 pr-3 font-medium">Task moves</th>
                      <th className="py-2 pr-3 font-medium">Closed</th>
                      <th className="py-2 pr-3 font-medium">Comments</th>
                      <th className="py-2 pr-3 font-medium">Open / overdue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.members.map((m) => (
                      <tr
                        key={m.memberUserId}
                        className="border-b last:border-0"
                        data-testid={`row-leaderboard-${m.memberUserId}`}
                      >
                        <td className="py-2 pr-3">
                          <div className="font-medium">{m.name}</div>
                          <div className="text-xs text-muted-foreground capitalize">{m.role}</div>
                        </td>
                        <td className="py-2 pr-3">{m.firstResponses.count}</td>
                        <td className="py-2 pr-3">{fmtMins(m.firstResponses.avgMins)}</td>
                        <td className="py-2 pr-3">
                          <SlaChip avgMins={m.firstResponses.avgMins} targetMins={data.sla.firstResponseMins} />
                        </td>
                        <td className="py-2 pr-3">{m.resolves.count}</td>
                        <td className="py-2 pr-3">{fmtMins(m.resolves.avgResolveMins)}</td>
                        <td className="py-2 pr-3">{m.volume.humanSends}</td>
                        <td className="py-2 pr-3">{m.volume.taskStatusChanges}</td>
                        <td className="py-2 pr-3">{m.volume.tasksClosed}</td>
                        <td className="py-2 pr-3">{m.volume.commentsPosted}</td>
                        <td className="py-2 pr-3">
                          {m.backlog.assignedOpen}
                          {m.backlog.overdue > 0 && (
                            <span className="text-red-600"> / {m.backlog.overdue} overdue</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── Trends tab ──────────────────────────────────────────────────────────────

const RANGE_PRESETS = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
] as const;

function TrendsTab() {
  const [rangeDays, setRangeDays] = useState<number>(30);
  const [grain, setGrain] = useState<"day" | "week" | "month">("day");
  const [overlayMemberId, setOverlayMemberId] = useState<string>("none");
  const from = daysAgoStr(rangeDays - 1);
  const to = localDateStr(new Date());

  const { data, isLoading, error } = useQuery<TrendResult>({
    queryKey: ["/api/app/team-stats/trend", grain, from, to],
    queryFn: () =>
      fetch(`/api/app/team-stats/trend?grain=${grain}&from=${from}&to=${to}`, {
        credentials: "include",
      }).then((r) => {
        if (!r.ok) throw new Error(`Request failed (${r.status})`);
        return r.json();
      }),
  });

  const overlay =
    data && overlayMemberId !== "none"
      ? data.byMember.find((m) => String(m.memberUserId) === overlayMemberId)
      : undefined;

  const chartData =
    data?.account.map((p) => ({
      ...p,
      memberAvgFirstResponseMins:
        overlay?.series.find((s) => s.bucket === p.bucket)?.avgFirstResponseMins ?? null,
    })) ?? [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={String(rangeDays)} onValueChange={(v) => setRangeDays(Number(v))}>
          <SelectTrigger className="w-40" data-testid="select-trend-range">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANGE_PRESETS.map((p) => (
              <SelectItem key={p.days} value={String(p.days)}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={grain} onValueChange={(v) => setGrain(v as typeof grain)}>
          <SelectTrigger className="w-32" data-testid="select-trend-grain">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Daily</SelectItem>
            <SelectItem value="week">Weekly</SelectItem>
            <SelectItem value="month">Monthly</SelectItem>
          </SelectContent>
        </Select>
        <Select value={overlayMemberId} onValueChange={setOverlayMemberId}>
          <SelectTrigger className="w-52" data-testid="select-trend-member">
            <SelectValue placeholder="Compare a member" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No member overlay</SelectItem>
            {data?.byMember.map((m) => (
              <SelectItem key={m.memberUserId} value={String(m.memberUserId)}>
                {m.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <LoadingPanel />
      ) : error || !data ? (
        <EmptyPanel message="Could not load trends. Try again shortly." />
      ) : (
        <>
          <Card data-testid="card-trend-response-times">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Response &amp; resolve times</CardTitle>
              <CardDescription>
                Account-wide averages in minutes{overlay ? ` · overlay: ${overlay.name}` : ""}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => fmtMins(v)} width={56} />
                  <Tooltip formatter={(v: number, name: string) => [fmtMins(v), name]} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="avgFirstResponseMins"
                    name="Avg first response"
                    stroke={CHART_BLUE}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="avgResolveMins"
                    name="Avg resolve"
                    stroke={CHART_GREEN}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                  {overlay && (
                    <Line
                      type="monotone"
                      dataKey="memberAvgFirstResponseMins"
                      name={`${overlay.name} — first response`}
                      stroke={CHART_BLUE}
                      strokeWidth={2}
                      strokeDasharray="6 4"
                      dot={false}
                      connectNulls
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card data-testid="card-trend-volume">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Outbound volume</CardTitle>
              <CardDescription>Team (human) sends vs. automated sends per {grain}</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.account} margin={{ top: 4, right: 4, left: 0, bottom: 0 }} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={40} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="humanSends" name="Team sends" fill={CHART_GREEN} radius={[4, 4, 0, 0]} />
                  <Bar
                    dataKey="totalSends"
                    name="All sends (incl. automated)"
                    fill={CHART_AMBER}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── Tickets tab ─────────────────────────────────────────────────────────────

function TicketsTab() {
  const [rangeDays, setRangeDays] = useState<number>(30);
  const [category, setCategory] = useState<string>("all");
  const from = daysAgoStr(rangeDays - 1);
  const to = localDateStr(new Date());

  const categoryParam = category === "all" ? "" : `&category=${encodeURIComponent(category)}`;
  const { data, isLoading, error } = useQuery<TicketStatsResponse>({
    queryKey: ["/api/app/team-stats/tickets", from, to, category],
    queryFn: () =>
      fetch(`/api/app/team-stats/tickets?from=${from}&to=${to}${categoryParam}`, {
        credentials: "include",
      }).then((r) => {
        if (!r.ok) throw new Error(`Request failed (${r.status})`);
        return r.json();
      }),
  });

  const chartRows =
    data?.byCategory.map((c) => ({
      category: c.category,
      close: c.avgTimeToCloseMins,
      touch: c.avgTimeToFirstTouchMins,
    })) ?? [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={String(rangeDays)} onValueChange={(v) => setRangeDays(Number(v))}>
          <SelectTrigger className="w-40" data-testid="select-tickets-range">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANGE_PRESETS.map((p) => (
              <SelectItem key={p.days} value={String(p.days)}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-44" data-testid="select-tickets-category">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {(data?.knownCategories ?? []).map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <LoadingPanel />
      ) : error || !data ? (
        <EmptyPanel message="Could not load ticket stats. Try again shortly." />
      ) : data.overall.ticketCount === 0 ? (
        <EmptyPanel message="No tickets were created or closed in this range." />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <KpiCard
              title="Avg Time to Close"
              value={fmtMins(data.overall.avgTimeToCloseMins)}
              icon={CheckCircle2}
              sub={`closed ${data.overall.closedCount} of ${data.overall.ticketCount}`}
            />
            <KpiCard
              title="Avg Time to First Touch"
              value={fmtMins(data.overall.avgTimeToFirstTouchMins)}
              icon={Timer}
              sub={`touched ${data.overall.touchedCount} of ${data.overall.ticketCount}`}
            />
            <KpiCard title="Tickets in Range" value={String(data.overall.ticketCount)} icon={Ticket} />
          </div>

          <Card data-testid="card-tickets-by-category">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">By category</CardTitle>
              <CardDescription>Average minutes to first touch and to close</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {chartRows.length > 1 && (
                <ResponsiveContainer width="100%" height={Math.max(160, chartRows.length * 56)}>
                  <BarChart
                    data={chartRows}
                    layout="vertical"
                    margin={{ top: 4, right: 8, left: 8, bottom: 0 }}
                    barGap={2}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v: number) => fmtMins(v)} />
                    <YAxis type="category" dataKey="category" tick={{ fontSize: 11 }} width={100} />
                    <Tooltip formatter={(v: number, name: string) => [fmtMins(v), name]} />
                    <Legend />
                    <Bar dataKey="touch" name="First touch" fill={CHART_BLUE} radius={[0, 4, 4, 0]} />
                    <Bar dataKey="close" name="Close" fill={CHART_GREEN} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="table-tickets-by-category">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground border-b">
                      <th className="py-2 pr-3 font-medium">Category</th>
                      <th className="py-2 pr-3 font-medium">Tickets</th>
                      <th className="py-2 pr-3 font-medium">Closed</th>
                      <th className="py-2 pr-3 font-medium">Avg first touch</th>
                      <th className="py-2 pr-3 font-medium">Avg close</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byCategory.map((c) => (
                      <tr key={c.category} className="border-b last:border-0" data-testid={`row-ticket-cat-${c.category}`}>
                        <td className="py-2 pr-3 font-medium">{c.category}</td>
                        <td className="py-2 pr-3">{c.ticketCount}</td>
                        <td className="py-2 pr-3">
                          {c.closedCount} of {c.ticketCount}
                        </td>
                        <td className="py-2 pr-3">{fmtMins(c.avgTimeToFirstTouchMins)}</td>
                        <td className="py-2 pr-3">{fmtMins(c.avgTimeToCloseMins)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── Settings tab ────────────────────────────────────────────────────────────

const settingsFormSchema = z.object({
  firstResponseTargetMins: z.coerce.number().int().min(1).max(7 * 24 * 60),
  resolveTargetMins: z.coerce.number().int().min(1).max(30 * 24 * 60),
  countAutomatedForAccountSla: z.boolean(),
  leaderboardMetric: z.enum(["first_response", "resolve", "volume"]),
});
type SettingsFormValues = z.infer<typeof settingsFormSchema>;

function SettingsTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery<TeamStatsSettings>({
    queryKey: ["/api/app/team-stats/settings"],
  });

  if (isLoading || !data) return <LoadingPanel />;
  return <SettingsForm initial={data} onSaved={() => {
    queryClient.invalidateQueries({ queryKey: ["/api/app/team-stats/settings"] });
    queryClient.invalidateQueries({ queryKey: ["/api/app/team-stats/day"] });
    queryClient.invalidateQueries({ queryKey: ["/api/app/team-stats/trend"] });
    toast({ title: "Settings saved" });
  }} />;
}

function SettingsForm({ initial, onSaved }: { initial: TeamStatsSettings; onSaved: () => void }) {
  const { toast } = useToast();
  const form = useForm<SettingsFormValues>({
    resolver: zodResolver(settingsFormSchema),
    defaultValues: initial,
  });
  const mutation = useMutation({
    mutationFn: (values: SettingsFormValues) =>
      apiRequest("PUT", "/api/app/team-stats/settings", values),
    onSuccess: onSaved,
    onError: (err) =>
      toast({
        title: "Could not save settings",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      }),
  });

  return (
    <Card className="max-w-xl" data-testid="card-team-stats-settings">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">SLA targets &amp; leaderboard</CardTitle>
        <CardDescription>
          Targets flag members and days as on/over target. Automated sends never count toward a
          member's personal stats.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="firstResponseTargetMins"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First-response target (minutes)</FormLabel>
                  <FormControl>
                    <Input type="number" min={1} {...field} data-testid="input-sla-first-response" />
                  </FormControl>
                  <FormDescription>How quickly an inbound member message should get its first reply.</FormDescription>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="resolveTargetMins"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Resolve target (minutes)</FormLabel>
                  <FormControl>
                    <Input type="number" min={1} {...field} data-testid="input-sla-resolve" />
                  </FormControl>
                  <FormDescription>How quickly a message or inbox item should be fully resolved.</FormDescription>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="leaderboardMetric"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Rank leaderboard by</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger data-testid="select-leaderboard-metric">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="first_response">Avg first response</SelectItem>
                      <SelectItem value="resolve">Avg time to resolve</SelectItem>
                      <SelectItem value="volume">Volume handled</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="countAutomatedForAccountSla"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div className="pr-4">
                    <FormLabel>Count automated replies toward account SLA</FormLabel>
                    <FormDescription>
                      When on, a messaging-rule or AI reply stops the account-wide response clock.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      data-testid="switch-count-automated"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <Button type="submit" disabled={mutation.isPending} data-testid="button-save-team-stats-settings">
              {mutation.isPending ? "Saving…" : "Save settings"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AppTeamStats() {
  const meQuery = useQuery<{
    activeMembership?: { role: string } | null;
  }>({
    queryKey: ["/api/app/auth/me"],
    staleTime: 60_000,
  });

  // Owner (no active membership) or admin-role teammate only — mirrors the
  // server's resolveTeamStatsScope 403. Fail closed while /me loads. No tier
  // gate: Team Stats is core functionality on every account tier (see the
  // route file's header comment).
  const membership = meQuery.data?.activeMembership ?? null;
  const roleAllowed = meQuery.isSuccess && (membership == null || canManageTeam(membership.role));

  return (
    <AppLayout>
      {meQuery.isLoading ? (
        <div className="p-6 space-y-2" data-testid="loading-team-stats">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-3/4" />
        </div>
      ) : !roleAllowed ? (
        <div
          className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center px-6"
          data-testid="role-gate-team-stats"
        >
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50">
            <ShieldAlert className="h-8 w-8 text-amber-500" />
          </div>
          <h1 className="text-2xl font-bold">Team Stats</h1>
          <p className="text-sm text-muted-foreground max-w-md">
            Only account owners and admins can view team performance stats. Ask your account
            owner for access if you need this view.
          </p>
        </div>
      ) : (
          <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold tracking-tight" data-tour="team-stats-heading">
                  Team Stats
                </h1>
                <p className="text-muted-foreground text-sm mt-0.5">
                  Response times · Resolves · Task throughput · Per-member performance
                </p>
              </div>
              <div className="flex items-center gap-2">
                <ExplainerVideoButton pageKey="team-stats" />
                <GuideButton pageKey="team-stats" />
              </div>
            </div>

            <GuideNudgeBanner pageKey="team-stats" />

            <Tabs defaultValue="day" className="space-y-5">
              <TabsList className="flex flex-wrap h-auto gap-1 p-1" data-tour="team-stats-tabs">
                <TabsTrigger value="day" className="gap-1.5" data-testid="tab-day">
                  <CalendarDays className="h-4 w-4" />
                  Day
                </TabsTrigger>
                <TabsTrigger value="trends" className="gap-1.5" data-testid="tab-trends">
                  <TrendingUp className="h-4 w-4" />
                  Trends
                </TabsTrigger>
                <TabsTrigger value="tickets" className="gap-1.5" data-testid="tab-tickets">
                  <Ticket className="h-4 w-4" />
                  Tickets
                </TabsTrigger>
                <TabsTrigger value="settings" className="gap-1.5" data-testid="tab-settings">
                  <Settings className="h-4 w-4" />
                  Settings
                </TabsTrigger>
              </TabsList>

              <TabsContent value="day" className="mt-0" data-tour="team-stats-day">
                <DayTab />
              </TabsContent>
              <TabsContent value="trends" className="mt-0">
                <TrendsTab />
              </TabsContent>
              <TabsContent value="tickets" className="mt-0">
                <TicketsTab />
              </TabsContent>
              <TabsContent value="settings" className="mt-0">
                <SettingsTab />
              </TabsContent>
            </Tabs>
          </div>
      )}
    </AppLayout>
  );
}
